import { createImageVariantConfig } from '../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { constructQueryParamName } from '../../util/search';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  onHandlePostRegistraion,
  onSendEndorsementRequest,
  onSendInvite,
  onSendEmail,
} from '../../util/api';

// Pagination page size
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchTribePage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchTribePage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchTribePage/SEARCH_LISTINGS_ERROR';

export const SEARCH_SUGGESTIONS_REQUEST = 'app/SearchTribePage/SEARCH_SUGGESTIONS_REQUEST';
export const SEARCH_SUGGESTIONS_SUCCESS = 'app/SearchTribePage/SEARCH_SUGGESTIONS_SUCCESS';
export const SEARCH_SUGGESTIONS_ERROR = 'app/SearchTribePage/SEARCH_SUGGESTIONS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchTribePage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchTribePage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchTribePage/SEARCH_MAP_LISTINGS_ERROR';

export const SEND_INVITE_REQUEST = 'app/SearchTribePage/SEND_INVITE_REQUEST';
export const SEND_INVITE_SUCCESS = 'app/SearchTribePage/SEND_INVITE_SUCCESS';
export const SEND_INVITE_ERROR = 'app/SearchTribePage/SEND_INVITE_ERROR';

export const SEND_ENDORSEMENT_REQUEST_PENDING =
  'app/SearchTribePage/SEND_ENDORSEMENT_REQUEST_PENDING';
export const SEND_ENDORSEMENT_REQUEST_SUCCESS =
  'app/SearchTribePage/SEND_ENDORSEMENT_REQUEST_SUCCESS';
export const SEND_ENDORSEMENT_REQUEST_ERROR = 'app/SearchTribePage/SEND_ENDORSEMENT_REQUEST_ERROR';

export const HANDLE_POST_REGISTRATION_REQUEST =
  'app/SearchTribePage/HANDLE_POST_REGISTRATION_REQUEST';
export const HANDLE_POST_REGISTRATION_SUCCESS =
  'app/SearchTribePage/HANDLE_POST_REGISTRATION_SUCCESS';
export const HANDLE_POST_REGISTRATION_ERROR = 'app/SearchTribePage/HANDLE_POST_REGISTRATION_ERROR';

export const SEND_EMAIL_REQUEST = 'app/SearchTribePage/SEND_EMAIL_REQUEST';
export const SEND_EMAIL_SUCCESS = 'app/SearchTribePage/SEND_EMAIL_SUCCESS';
export const SEND_EMAIL_ERROR = 'app/SearchTribePage/SEND_EMAIL_ERROR';

export const SEARCH_ENDORSEMENTS_LISTINGS_REQUEST =
  'app/SearchTribePage/SEARCH_ENDORSEMENTS_LISTINGS_REQUEST';
export const SEARCH_ENDORSEMENTS_LISTINGS_SUCCESS =
  'app/SearchTribePage/SEARCH_ENDORSEMENTS_LISTINGS_SUCCESS';
export const SEARCH_ENDORSEMENTS_LISTINGS_ERROR =
  'app/SearchTribePage/SEARCH_ENDORSEMENTS_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  suggestionsInProgress: false,
  suggestionsResultIds: [],
  suggestionsError: null,
  searchSuccess: false,
  sendInviteInProgress: false,
  sendInviteSuccessMsg: null,
  sendInviteErrorMsg: null,
  postRegistrationInProgress: false,
  postRegistrationSuccessMsg: null,
  postRegistrationErrorMsg: null,

  sendEmailInProgress: false,
  sendEmailSuccessMsg: null,
  sendEmailErrorMsg: null,

  endorsementsListingsResultIds: [],
  endorsementListingsInProgress: false,
  endorsementsListingsError: null,
};

const resultIds = data => {
  const listings = data.data;
  return listings
    .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
    .map(l => l.id);
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchSuccess: false,
        searchMapListingIds: [],
        searchListingsError: null,
        sendInviteInProgress: false,
        sendInviteSuccessMsg: null,
        sendInviteErrorMsg: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
        searchSuccess: true,
      };
    case SEARCH_LISTINGS_ERROR:
      console.error(payload);
      return {
        ...state,
        searchInProgress: false,
        searchSuccess: false,
        searchListingsError: payload,
      };

    case SEARCH_SUGGESTIONS_REQUEST:
      return { ...state, suggestionsInProgress: true };
    case SEARCH_SUGGESTIONS_SUCCESS:
      return {
        ...state,
        suggestionsResultIds: resultIds(payload.data),
        suggestionsInProgress: false,
      };
    case SEARCH_SUGGESTIONS_ERROR:
      console.error(payload);
      return { ...state, suggestionsInProgress: false, suggestionsError: payload };

    // case SEARCH_MAP_SET_ACTIVE_LISTING:
    //   return { ...state, activeListingId: payload };

    case SEND_INVITE_REQUEST:
      return {
        ...state,
        sendInviteInProgress: true,
        sendInviteSuccessMsg: null,
        sendInviteErrorMsg: null,
      };
    case SEND_INVITE_SUCCESS:
      return {
        ...state,
        sendInviteInProgress: false,
        sendInviteSuccessMsg: payload,
        sendInviteErrorMsg: null,
      };
    case SEND_INVITE_ERROR:
      return {
        ...state,
        sendInviteInProgress: false,
        sendInviteSuccessMsg: null,
        sendInviteErrorMsg: payload,
      };

    case SEND_ENDORSEMENT_REQUEST_PENDING:
      return { ...state };
    case SEND_ENDORSEMENT_REQUEST_SUCCESS:
      return { ...state };
    case SEND_ENDORSEMENT_REQUEST_ERROR:
      return { ...state };

    case HANDLE_POST_REGISTRATION_REQUEST:
      return {
        ...state,
        postRegistrationInProgress: true,
        postRegistrationSuccessMsg: null,
        postRegistrationErrorMsg: null,
      };
    case HANDLE_POST_REGISTRATION_SUCCESS:
      return {
        ...state,
        postRegistrationInProgress: false,
        postRegistrationSuccessMsg: payload,
        postRegistrationErrorMsg: null,
      };
    case HANDLE_POST_REGISTRATION_ERROR:
      return {
        ...state,
        postRegistrationInProgress: false,
        postRegistrationSuccessMsg: null,
        postRegistrationErrorMsg: payload,
      };

    case SEND_EMAIL_REQUEST:
      return {
        ...state,
        sendEmailInProgress: true,
        sendEmailSuccessMsg: null,
        sendEmailErrorMsg: null,
      };
    case SEND_EMAIL_SUCCESS:
      return {
        ...state,
        sendEmailInProgress: false,
        sendEmailSuccessMsg: payload,
        sendEmailErrorMsg: null,
      };
    case SEND_EMAIL_ERROR:
      return {
        ...state,
        sendEmailInProgress: false,
        sendEmailSuccessMsg: null,
        sendEmailErrorMsg: payload,
      };

    case SEARCH_ENDORSEMENTS_LISTINGS_REQUEST:
      return { ...state, endorsementListingsInProgress: true, endorsementsListingsError: null };
    case SEARCH_ENDORSEMENTS_LISTINGS_SUCCESS: {
      return {
        ...state,
        endorsementsListingsResultIds: payload.map(data => resultIds(data.data)).flat(),
        endorsementListingsInProgress: false,
      };
    }
    case SEARCH_ENDORSEMENTS_LISTINGS_ERROR:
      return { ...state, endorsementListingsInProgress: false, endorsementsListingsError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchSuggestionsRequest = () => ({
  type: SEARCH_SUGGESTIONS_REQUEST,
});

export const searchSuggestionsSuccess = response => ({
  type: SEARCH_SUGGESTIONS_SUCCESS,
  payload: { data: response.data },
});

export const searchSuggestionsError = e => ({
  type: SEARCH_SUGGESTIONS_ERROR,
  error: true,
  payload: e,
});

export const querySendInvitePending = () => ({
  type: SEND_INVITE_REQUEST,
});

export const querySendInviteSuccess = msg => ({
  type: SEND_INVITE_SUCCESS,
  payload: msg,
});

export const querySendInviteError = msg => ({
  type: SEND_INVITE_ERROR,
  payload: msg,
});

export const querySendEndorsementPending = () => ({
  type: SEND_ENDORSEMENT_REQUEST_PENDING,
});

export const querySendEndorsementSuccess = () => ({
  type: SEND_ENDORSEMENT_REQUEST_SUCCESS,
});

export const querySendEndorsementError = () => ({
  type: SEND_ENDORSEMENT_REQUEST_ERROR,
});

export const queryHandlePostRegistrationPending = () => ({
  type: HANDLE_POST_REGISTRATION_REQUEST,
});

export const queryHandlePostRegistrationSuccess = msg => ({
  type: HANDLE_POST_REGISTRATION_SUCCESS,
  payload: msg,
});

export const queryHandlePostRegistrationError = msg => ({
  type: HANDLE_POST_REGISTRATION_ERROR,
  payload: msg,
});

export const querySendEmailPending = () => ({
  type: SEND_EMAIL_REQUEST,
});
export const querySendEmailSuccess = payload => ({
  type: SEND_EMAIL_SUCCESS,
  payload,
});
export const querySendEmailError = payload => ({
  type: SEND_EMAIL_ERROR,
  payload,
});

export const searchEndorsementsListingsRequest = () => ({
  type: SEARCH_ENDORSEMENTS_LISTINGS_REQUEST,
});

export const searchEndorsementsListingsSuccess = response => ({
  type: SEARCH_ENDORSEMENTS_LISTINGS_SUCCESS,
  payload: response,
});

export const searchEndorsementsListingsError = e => ({
  type: SEARCH_ENDORSEMENTS_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Action to send an invitation
export const sendInviteToJoinSystem = data => async (dispatch, getState, sdk) => {
  const params = {
    to: data?.email,
    subject: 'Invitation and endorsement to join Munay',
    url: data.url,
    text: data?.yourEndorsment,
    yourEndorsment: data?.yourEndorsment,
    userId: data?.userId,
  };
  dispatch(querySendInvitePending());
  try {
    const response = await onSendInvite(params);
    dispatch(querySendInviteSuccess(`${data?.email} was invited successfully.`));
    return response;
  } catch (error) {
    dispatch(querySendInviteError('Failed to send invite!'));
    throw error;
  }
};

export const sendDisconnectFromTribeEmail = params => async dispatch => {
  const { senderId, receiverId } = params;

  const data1 = {
    senderId,
    receiverId,
    text: 'Tribe Connection Removed',
    emailId: 100039,
  };
  const data2 = {
    ...data1,
    senderId: receiverId,
    receiverId: senderId,
  };
  dispatch(querySendEmailPending());

  try {
    await onSendEmail(data1);
    await onSendEmail(data2);
    return dispatch(querySendEmailSuccess(`User was successfully disconnected from Tribe`));
  } catch (error) {
    dispatch(querySendEmailError('Failed to send email!'));
    throw error;
  }
};

// Action to handle endorsement request after registration
export const handlePostRegistration = ({ inviteToken, invitedBy, newUserId }) => async (
  dispatch,
  getState,
  sdk
) => {
  dispatch(queryHandlePostRegistrationPending());
  const body = { inviteToken, invitedBy, newUserId };
  try {
    const response = await onHandlePostRegistraion(body);
    dispatch(queryHandlePostRegistrationSuccess(response?.message));
    return response;
  } catch (error) {
    dispatch(queryHandlePostRegistrationError('Failed to create endorsement request!'));
    throw error;
  }
};

// Action to send an endorsement request
export const sendEndorsemntInvite = data => async dispatch => {
  dispatch(querySendEndorsementPending());
  try {
    await onSendEndorsementRequest(data);
    dispatch(querySendEndorsementSuccess());
  } catch (error) {
    dispatch(querySendEndorsementError());
    throw error;
  }
};

export const searchListings = (endorsements, config) => (dispatch, getState, sdk) => {
  dispatch(searchEndorsementsListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    pub_listingType: 'profileListing',
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.location',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  const promises = endorsements.map(({ id, senderId }) => {
    return sdk.listings.query({
      ...params,
      authorId: id?.uuid ?? senderId,
    });
  });

  return Promise.all(promises)
    .then(responses => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      responses.forEach(r => dispatch(addMarketplaceEntities(r, sanitizeConfig)));
      dispatch(searchEndorsementsListingsSuccess(responses));

      return responses;
    })
    .catch(e => {
      dispatch(searchEndorsementsListingsError(storableError(e)));
    });
};

// Search tribe listings
export const searchTribeListings = (searchParams, config, isSuggestions) => (
  dispatch,
  getState,
  sdk
) => {
  if (isSuggestions) {
    dispatch(searchSuggestionsRequest());
  } else {
    dispatch(searchListingsRequest(searchParams));
  }

  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
        }
      : {};
  };

  const omitInvalidCategoryParams = params => {
    const categoryConfig = config.search.defaultFilters?.find(f => f.schemaType === 'category');
    const categories = config.categoryConfiguration.categories;
    const { key: prefix, scope } = categoryConfig || {};
    const categoryParamPrefix = constructQueryParamName(prefix, scope);

    const validURLParamForCategoryData = (prefix, categories, level, params) => {
      const levelKey = `${categoryParamPrefix}${level}`;
      const levelValue = params?.[levelKey];
      const foundCategory = categories.find(cat => cat.id === levelValue);
      const subcategories = foundCategory?.subcategories || [];
      return foundCategory && subcategories.length > 0
        ? {
            [levelKey]: levelValue,
            ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
          }
        : foundCategory
        ? { [levelKey]: levelValue }
        : {};
    };

    const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
    const nonCategoryKeys = Object.entries(params).reduce(
      (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
      {}
    );

    return { ...nonCategoryKeys, ...categoryKeys };
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          availability: 'time-partial',
          minDuration,
        }
      : {};
  };

  const stockFilters = datesMaybe => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;
    return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
  };

  const seatsSearchParams = (seats, datesMaybe) => {
    const seatsFilter = config.search.defaultFilters.find(f => f.key === 'seats');
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;
    return hasDatesFilterInUse && seatsFilter ? { seats } : {};
  };

  const {
    keywords,
    id,
    perPage,
    price,
    dates,
    seats,
    sort,
    mapSearch,
    ...restOfParams
  } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe);
  const seatsMaybe = seatsSearchParams(seats, datesMaybe);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    ...omitInvalidCategoryParams(restOfParams),
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...seatsMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
    keywords,
    pub_listingType: 'profileListing',
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'author.profileImage', 'images'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'deleted',
      'state',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.location',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  if (id) {
    params.ids = id;
  }

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      if (isSuggestions) {
        dispatch(searchSuggestionsSuccess(response));
      } else {
        dispatch(searchListingsSuccess(response));
      }
      return response;
    })
    .catch(e => {
      const error = storableError(e);
      if (isSuggestions) {
        dispatch(searchSuggestionsError(error));
      } else {
        dispatch(searchListingsError(error));
      }
      if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
        throw e;
      }
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const loadData = (params, search, config) => (dispatch, getState) => {
  const state = getState();
  const { currentUserEndorsementsRequest } = state.user;

  return dispatch(searchListings(currentUserEndorsementsRequest, config));
};
